import { useEffect, useState } from 'react';
import labourService from '../../services/labour';
import { errorMessage } from '../../utils/apiResponse';
import type { TimesheetCheckRow } from '../../types/labour';
import { DateRangeInputs, Notice } from './shared';
import { hrs, useDateRange } from './labourUtils';

/**
 * Timesheet hours vs time on jobs, per app user per day. The gap is time paid
 * but not on any job or cost centre — travel, waiting for bins, the shed.
 * Shown so a supervisor can allocate it; never spread silently.
 */
export default function TimesheetCheckPanel() {
  const range = useDateRange();
  const [rows, setRows] = useState<TimesheetCheckRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gapsOnly, setGapsOnly] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    labourService.getTimesheetCheck(range.from, range.to)
      .then(r => { if (live) { setRows(r); setError(null); } })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load the check')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [range.from, range.to]);

  const shown = gapsOnly ? rows.filter(r => Math.abs(r.unallocated_hours) >= 0.25) : rows;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeInputs {...range} />
        <label className="flex items-center gap-2 text-sm cursor-pointer pb-2">
          <input type="checkbox" checked={gapsOnly} onChange={e => setGapsOnly(e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-600">Only days that don’t match</span>
        </label>
      </div>
      <p className="text-sm text-neutral-500">
        People with the app only. A positive gap is paid time not on a job; a negative one is job time with no
        timesheet behind it.
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      {loading ? (
        <p className="text-sm text-neutral-500 py-6 text-center">Checking…</p>
      ) : shown.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">{rows.length ? 'Every day matches.' : 'No timesheets in this range.'}</p>
      ) : (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Person</th>
                <th className="text-right px-3 py-2 font-medium">Timesheet</th>
                <th className="text-right px-3 py-2 font-medium">On jobs</th>
                <th className="text-right px-3 py-2 font-medium">Gap</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {shown.map(r => (
                <tr key={`${r.worker_id}-${r.date}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{r.worker_name}{r.still_clocked_in && <span className="text-xs text-neutral-500"> (still clocked in)</span>}</td>
                  <td className="px-3 py-2 text-right">{hrs(r.timesheet_hours)}</td>
                  <td className="px-3 py-2 text-right">{hrs(r.job_hours)}</td>
                  <td className={`px-3 py-2 text-right font-medium ${Math.abs(r.unallocated_hours) >= 0.25 ? 'text-amber-700' : 'text-neutral-500'}`}>
                    {hrs(r.unallocated_hours)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
